import { css } from "@emotion/react"
import { useIsChurchCenterApp } from "source/Layout"
import { formatDuration } from "./utils"

interface ScrubberProps {
  currentTime: number
  duration: number
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  children?: React.ReactNode
}

export function Scrubber({
  currentTime,
  duration,
  onChange,
  onKeyDown,
  children,
}: ScrubberProps) {
  const isChurchCenterApp = useIsChurchCenterApp()

  return (
    <div css={styles.wrapper}>
      <progress
        aria-hidden="true"
        css={styles.progressBar}
        max={duration}
        value={currentTime.toFixed(4)}
      />
      <input
        type="range"
        aria-label="Playback position"
        aria-valuetext={`${formatDuration(currentTime)} of ${formatDuration(duration)}`}
        css={styles.progressInput}
        disabled={isChurchCenterApp}
        onChange={onChange}
        onKeyDown={onKeyDown}
        value={currentTime.toFixed(4)}
        min={0}
        max={duration}
        step="any"
      />
      {children}
    </div>
  )
}

const thumbProperties = css`
  -webkit-appearance: none;
  opacity: 0;
  width: 16px;
  height: 16px;
  border-radius: 8px;
  cursor: pointer;
  background-color: var(--color-brand);
  box-shadow: none;
  transition:
    opacity 0.2s ease-in,
    transform 0.2s ease-in;
  border: none;
  transform: scale(0);
  transform-origin: center center;
`

const styles = {
  wrapper: css`
    flex: 1;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  progressInput: css`
    -webkit-appearance: none;
    height: 8px;
    margin: 0;
    background: transparent;
    position: absolute;
    top: 50%;
    left: -8px;
    right: -8px;
    transform: translateY(-50%);
    z-index: 100;
    cursor: pointer;
    border-radius: 4px;

    &::-webkit-slider-thumb {
      ${thumbProperties}
    }

    &::-moz-range-thumb {
      ${thumbProperties}
    }

    &::-moz-range-track {
      background: transparent;
    }
    &::-ms-range-track {
      background: transparent;
    }

    &:hover {
      &::-webkit-slider-thumb {
        opacity: 1;
        transform: scale(1);
      }
      &::-moz-range-thumb {
        opacity: 1;
        transform: scale(1);
      }
    }
  `,
  progressBar: css`
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border: none;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background-color: var(--scrubber-track-color, var(--color-static-gray100));
    overflow: hidden;
    border: 1px solid var(--color-tint5);

    &::-webkit-progress-bar {
      background-color: var(
        --scrubber-track-color,
        var(--color-static-gray100)
      );
      border-radius: 4px;
    }

    &::-moz-progress-bar {
      background-color: var(--color-brand);
      border-radius: 4px;
    }

    &::-webkit-progress-value {
      background-color: var(--color-brand);
      border-radius: 4px;
    }
  `,
}
